import React, { useState } from 'react';
import { ClockIcon, CheckCircleIcon, TicketIcon, RefreshCwIcon, XCircleIcon, FilterIcon, SearchIcon, AlertTriangleIcon, ArrowRightIcon, UserIcon, MapPinIcon, ChevronUpIcon, MoreHorizontalIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
interface QueueCard {
  id: string;
  traveler: string;
  trip: string;
  destination: string;
  status: string;
  timeElapsed: string;
  priority: 'normal' | 'warning' | 'overdue';
  amount: string;
  department: string;
}
export function OperationsQueuePage() {
  const {
    operationsQueue
  } = useAppContext();
  const [filterClient, setFilterClient] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const columns = [{
    title: 'Pending Approvals',
    count: operationsQueue.pendingApprovals,
    color: 'border-amber-400',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    icon: ClockIcon,
    cards: [{
      id: 'REQ-2024-001',
      traveler: 'David Miller',
      trip: 'Tech Conference 2024',
      destination: 'San Francisco, CA',
      status: 'Awaiting Dept Head',
      timeElapsed: '18h',
      priority: 'warning' as const,
      amount: '$1,850',
      department: 'Engineering'
    }, {
      id: 'REQ-2024-002',
      traveler: 'Sarah Jenkins',
      trip: 'Client Meeting',
      destination: 'Chicago, IL',
      status: 'Awaiting Manager',
      timeElapsed: '5h',
      priority: 'normal' as const,
      amount: '$950',
      department: 'Sales'
    }]
  }, {
    title: 'Approved – Awaiting Ticketing',
    count: operationsQueue.awaitingTicketing,
    color: 'border-blue-400',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    icon: CheckCircleIcon,
    cards: [{
      id: 'REQ-2024-003',
      traveler: 'Michael Chen',
      trip: 'Team Offsite',
      destination: 'Austin, TX',
      status: 'Ready to ticket',
      timeElapsed: '2h',
      priority: 'normal' as const,
      amount: '$1,200',
      department: 'Product'
    }, {
      id: 'REQ-2024-008',
      traveler: 'Lisa Park',
      trip: 'Marketing Summit',
      destination: 'New York, NY',
      status: 'Pending vendor confirmation',
      timeElapsed: '6h',
      priority: 'normal' as const,
      amount: '$2,100',
      department: 'Marketing'
    }, {
      id: 'REQ-2024-009',
      traveler: 'James Wilson',
      trip: 'Client Onsite',
      destination: 'Seattle, WA',
      status: 'Ready to ticket',
      timeElapsed: '1h',
      priority: 'normal' as const,
      amount: '$780',
      department: 'Engineering'
    }, {
      id: 'REQ-2024-010',
      traveler: 'Anna Lee',
      trip: 'Training Workshop',
      destination: 'Denver, CO',
      status: 'Awaiting payment',
      timeElapsed: '28h',
      priority: 'overdue' as const,
      amount: '$650',
      department: 'HR'
    }]
  }, {
    title: 'Ticketed',
    count: operationsQueue.ticketed,
    color: 'border-emerald-400',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    icon: TicketIcon,
    cards: [{
      id: 'BK-7829',
      traveler: 'Sarah Connor',
      trip: 'London Business Trip',
      destination: 'London, UK',
      status: 'Ticket issued',
      timeElapsed: 'Completed',
      priority: 'normal' as const,
      amount: '$1,240',
      department: 'Sales'
    }, {
      id: 'BK-7830',
      traveler: 'John Smith',
      trip: 'NYC Conference',
      destination: 'New York, NY',
      status: 'Ticket issued',
      timeElapsed: 'Completed',
      priority: 'normal' as const,
      amount: '$850',
      department: 'Engineering'
    }]
  }, {
    title: 'Refunds',
    count: operationsQueue.refunds,
    color: 'border-purple-400',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    icon: RefreshCwIcon,
    cards: [{
      id: 'RF-2024-089',
      traveler: 'David Miller',
      trip: 'Cancelled Flight',
      destination: 'Boston, MA',
      status: 'Processing refund',
      timeElapsed: '3 days',
      priority: 'warning' as const,
      amount: '$450',
      department: 'Engineering'
    }, {
      id: 'RF-2024-090',
      traveler: 'Emily Chen',
      trip: 'Hotel Cancellation',
      destination: 'Miami, FL',
      status: 'Refund submitted',
      timeElapsed: '1 day',
      priority: 'normal' as const,
      amount: '$320',
      department: 'Finance'
    }]
  }, {
    title: 'Cancellations',
    count: operationsQueue.cancellations,
    color: 'border-red-400',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    icon: XCircleIcon,
    cards: [{
      id: 'CAN-2024-015',
      traveler: 'Mike Ross',
      trip: 'Cancelled Trip',
      destination: 'Dallas, TX',
      status: 'Cancellation confirmed',
      timeElapsed: '12h',
      priority: 'normal' as const,
      amount: '$1,100',
      department: 'Legal'
    }]
  }];
  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'overdue':
        return 'border-l-4 border-l-red-500 bg-red-50/50';
      case 'warning':
        return 'border-l-4 border-l-amber-500 bg-amber-50/30';
      default:
        return 'border-l-4 border-l-emerald-400';
    }
  };
  return <div className="space-y-6" data-id="element-1905">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1906">
        <div data-id="element-1907">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-1908">Operations Queue</h2>
          <p className="text-slate-500" data-id="element-1909">
            Live view of all travel operations across the pipeline.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1910">
          <div className="relative" data-id="element-1911">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-1912" />
            <input type="text" placeholder="Search operations..." className="h-9 w-48 rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" data-id="element-1913" />
          </div>
          <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-navy-600 focus:outline-none" data-id="element-1914">
            <option value="all" data-id="element-1915">All Departments</option>
            <option value="engineering" data-id="element-1916">Engineering</option>
            <option value="sales" data-id="element-1917">Sales</option>
            <option value="marketing" data-id="element-1918">Marketing</option>
            <option value="product" data-id="element-1919">Product</option>
            <option value="hr" data-id="element-1920">HR</option>
          </select>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-navy-600 focus:outline-none" data-id="element-1921">
            <option value="all" data-id="element-1922">All Clients</option>
            <option value="acme" data-id="element-1923">Acme Corp</option>
            <option value="globex" data-id="element-1924">Globex Inc</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-id="element-1925">
        {columns.map(col => <div key={col.title} className={`rounded-xl border bg-white p-4 shadow-sm ${col.color} border-t-4`} data-id="element-1926">
            <div className="flex items-center gap-2 mb-2" data-id="element-1927">
              <col.icon className={`h-4 w-4 ${col.textColor}`} data-id="element-1928" />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide truncate" data-id="element-1929">
                {col.title.split('–')[0].trim()}
              </span>
            </div>
            <p className="text-2xl font-bold text-navy-900" data-id="element-1930">{col.count}</p>
          </div>)}
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" data-id="element-1931">
        {columns.map(column => <div key={column.title} className="flex-shrink-0 w-72" data-id="element-1932">
            <div className={`rounded-t-xl px-4 py-3 ${column.bgColor} border border-b-0 ${column.color}`} data-id="element-1933">
              <div className="flex items-center justify-between" data-id="element-1934">
                <div className="flex items-center gap-2" data-id="element-1935">
                  <column.icon className={`h-4 w-4 ${column.textColor}`} data-id="element-1936" />
                  <h3 className={`text-sm font-bold ${column.textColor}`} data-id="element-1937">
                    {column.title}
                  </h3>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${column.bgColor} ${column.textColor}`} data-id="element-1938">
                  {column.cards.length}
                </span>
              </div>
            </div>
            <div className="bg-slate-50 border border-t-0 border-slate-200 rounded-b-xl p-3 space-y-3 kanban-column" data-id="element-1939">
              {column.cards.map(card => <div key={card.id} className={`rounded-lg bg-white p-3 shadow-sm border border-slate-200 ${getPriorityStyles(card.priority)} transition-shadow hover:shadow-md`} data-id="element-1940">
                  <div className="flex items-center justify-between mb-2" data-id="element-1941">
                    <span className="text-xs font-mono font-bold text-navy-700" data-id="element-1942">
                      {card.id}
                    </span>
                    {card.priority === 'overdue' && <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full" data-id="element-1943">
                        <AlertTriangleIcon className="h-3 w-3" data-id="element-1944" /> OVERDUE
                      </span>}
                    {card.priority === 'warning' && <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full" data-id="element-1945">
                        <ClockIcon className="h-3 w-3" data-id="element-1946" /> {card.timeElapsed}
                      </span>}
                    {card.priority === 'normal' && <span className="text-[10px] text-slate-400" data-id="element-1947">
                        {card.timeElapsed}
                      </span>}
                  </div>
                  <div className="flex items-center gap-2 mb-1.5" data-id="element-1948">
                    <div className="h-6 w-6 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0" data-id="element-1949">
                      <UserIcon className="h-3 w-3 text-navy-600" data-id="element-1950" />
                    </div>
                    <span className="text-sm font-semibold text-slate-900 truncate" data-id="element-1951">
                      {card.traveler}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mb-1 truncate" data-id="element-1952">
                    {card.trip}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-2" data-id="element-1953">
                    <MapPinIcon className="h-3 w-3" data-id="element-1954" />
                    <span className="truncate" data-id="element-1955">{card.destination}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100" data-id="element-1956">
                    <span className="text-xs font-bold text-navy-900" data-id="element-1957">
                      {card.amount}
                    </span>
                    <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-1958">
                      {card.department}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-1" data-id="element-1959">
                    {column.title.includes('Pending') && <>
                        <button className="flex-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded py-1 transition-colors" data-id="element-1960">
                          Approve
                        </button>
                        <button className="flex-1 text-[10px] font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded py-1 transition-colors" data-id="element-1961">
                          Escalate
                        </button>
                      </>}
                    {column.title.includes('Awaiting') && <button className="flex-1 text-[10px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded py-1 transition-colors" data-id="element-1962">
                        Issue Ticket
                      </button>}
                    {column.title.includes('Refund') && <button className="flex-1 text-[10px] font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded py-1 transition-colors" data-id="element-1963">
                        Process Refund
                      </button>}
                  </div>
                </div>)}
            </div>
          </div>)}
      </div>
    </div>;
}